import { useEffect, useState } from "react";
import experienceDetailBG from "assets/images/others/host-side-images/experienceDetailBG.png";
import { ReactComponent as LocationIcon } from "assets/images/svgs/locationDark.svg";
import { ReactComponent as CalandarIcon } from "assets/images/svgs/calendarDark.svg";
import { ReactComponent as ClockIcon } from "assets/images/svgs/clockdark.svg";
import { useNavigate } from "react-router-dom";
import { siteRoutes } from "utils/helpers/enums/routes";
import useUtils from "hooks/useUtils";
import { EventDTO } from "utils/helpers/models/shared/event.dto";
import { getFilePathWithBackendUrl } from "utils/helpers/common/http-methods";
import { concatPathWithBackendUrl } from "utils/helpers/urlHelper";
import WorkshopSessionPopup from "components/popups/workshop-session";
import { confirmationPopup } from "utils/helpers/common/alert-service";
import MinorBreadcrumb, {
  MinorBreadcrumbLink,
} from "components/breadcrumbs/MinorBreadcrumb";
import useStore from "hooks/useStore";
import { GENDERS } from "utils/helpers/enums/shared.enum";
import useMinor from "../useHooks";
import ExperienceDetailHeader from "components/experience-detail-header";
import ImageCarousel from "components/image-carousel";

const MinorExperiencesDetail = () => {
  const navigate = useNavigate();
  const breadcrumbLinks: MinorBreadcrumbLink[] = [
    { title: "Home ", path: siteRoutes.minorHome },
    { title: "Experiences Detail", path: siteRoutes.minorExperiencesDetail },
  ];
  const { getUserGender } = useStore();
  const userGender = getUserGender();
  const { getQueryParams } = useUtils();
  const [event, setEvent] = useState<EventDTO>(new EventDTO());
  const params = getQueryParams();

  const [workshopSessionModal, setWorkshopSessionModal] = useState(false);
  const { getEventById, addToWishlist, removeFromWishlist } = useMinor();
  const [exp, setExp] = useState<EventDTO>(event);

  const getEventDataByID = async () => {
    if (params?.id) {
      await getEventById(params?.id, setEvent);
    }
  };

  useEffect(() => {
    getEventDataByID();
  }, []);

  const now = new Date();
  const sessions = event.workshopSessions || [];

  // Sort sessions by startDateTime to get the actual first session
  const sortedSessions = [...sessions].sort((a, b) => 
    new Date(a.startDateTime).getTime() - new Date(b.startDateTime).getTime()
  );

  // Start check - using first session (earliest) start date and time
  const firstSession = sortedSessions.length > 0 ? sortedSessions[0] : null;
  const firstStart = firstSession ? new Date(firstSession.startDateTime) : null;
  const hasEventStarted =
    firstStart && !isNaN(firstStart.getTime()) && now >= firstStart;

  // Sort sessions by endDateTime to get the actual last session
  const sortedByEnd = [...sessions].sort((a, b) => 
    new Date(b.endDateTime).getTime() - new Date(a.endDateTime).getTime()
  );

  // End check - using last session (latest) end date and time
  const lastSession = sortedByEnd.length > 0 ? sortedByEnd[0] : null;
  const lastEnd = lastSession ? new Date(lastSession.endDateTime) : null;
  const hasEventEnded = lastEnd && !isNaN(lastEnd.getTime()) && now >= lastEnd;

  const visibleSessions = sortedSessions.slice(0, 3);
  const hasMoreSessions = sortedSessions.length > 3;

  const handleToggleWishlist = async (exp: any) => {
    const { id: expId } = exp;

    const result = await confirmationPopup(
      exp?.isWishlisted
        ? "Are you sure you want to remove from the wishlist?"
        : "Are you sure you want to add to the wishlist?"
    );

    if (!result.isConfirmed) return;

    if (exp?.isWishlisted) {
      await removeFromWishlist(expId);
      setExp((prev) => ({ ...prev, isWishlisted: false }));
    } else {
      await addToWishlist(expId);
      setExp((prev) => ({ ...prev, isWishlisted: true }));
    }

    await getEventDataByID();
  };

  const gotoTicketPage = (eventId: number) => {
    navigate(`${siteRoutes.minorTicket}?id=${eventId}`);
  };

  return (
    <div>
      <ExperienceDetailHeader
        event={event}
        breadcrumb={<MinorBreadcrumb links={breadcrumbLinks} />}
        // onHostClick={goToHostDetail}
        onSeeMoreSessions={() => setWorkshopSessionModal(true)}
        visibleSessions={visibleSessions}
        hasMoreSessions={hasMoreSessions}
        gradientClass="minor-bg-gradient"
      />

      <div className="container my-5">
        <div className="flex flex-wrap sm:flex-nowrap justify-between items-center gap-6 ">
          <div className="text-blackie text-3xl">Overview</div>
          <div className="flex gap-4">
            {event.availableSlots > 0 && !hasEventStarted && !hasEventEnded && (
              <button
                className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                onClick={() => handleToggleWishlist(event)}
              >
                {event?.isWishlisted
                  ? "Remove from Wishlist"
                  : "Add to Wishlist"}
              </button>
            )}
            {hasEventEnded === false && hasEventStarted === true && (
              <button
                className="px-5 py-3 flex gap-2 rounded-full text-white bg-primaryBlueBg cursor-pointer"
                onClick={() => gotoTicketPage(event.id)}
              >
                View Pass
              </button>
            )}
          </div>
        </div>
        <div className="w-full flex flex-wrap md:flex-nowrap my-5 md:flex-row flex-col-reverse md:gap-6">
          <div className="w-full md:w-[60%] mt-5 md:mt-0">
            <div>
              <h3 className="text-blackie text-xl font-medium mb-2">
                About Experience
              </h3>
              <p className="text-lightBlackText text-base">
                {event?.description || "Description"}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Age Group
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.ageGroup
                    ? event.ageGroup.replace(/_/g, " ")
                    : "Age Group"}
                </p>
              </div>
              <div>
                <div className="mb-4">
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Language
                  </h3>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-fade text-blackie rounded-lg text-base">
                      {event?.language || "Language"}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Skill Level
                </h3>
                <p className="text-lightBlackText text-base capitalize">
                  {event?.skillLevel.replaceAll("_", " ") || "Skill Level"}
                </p>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-medium text-blackie mb-2">
                Details
              </h2>

              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Learning Activity
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.description || ""}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Core Concepts
                  </h3>
                  <p className="text-lightBlackText text-base uppercase">
                    {event.coreConcept.join(", ")}
                  </p>
                </div>
                <div>
                  <h3 className="text-blackie text-xl font-medium mb-2">
                    Parent Supervision
                  </h3>
                  <p className="text-lightBlackText text-base">
                    {event?.parentSupervision ? "Yes" : "No"}
                  </p>
                </div>

                {event?.specialInstructions && (
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Special Instructions
                    </h3>

                    <p className="text-lightBlackText text-base">
                      {event?.specialInstructions
                        ? event.specialInstructions
                        : ""}
                    </p>
                  </div>
                )}

                {event?.physicalConsideration && (
                  <div>
                    <h3 className="text-blackie text-xl font-medium mb-2">
                      Physical Consideration
                    </h3>

                    <ul className="text-lightBlackText text-base list-disc list-inside">
                      {event?.physicalConsideration
                        ? event.physicalConsideration
                        : ""}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-blackie mb-6">
                Mission
              </h2>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2">
                  Mission
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.missionStatement || "--"}
                </p>
              </div>
              <div>
                <h3 className="text-blackie text-xl font-medium mb-2 mt-3">
                  About{" "}
                </h3>
                <p className="text-lightBlackText text-base">
                  {event?.host?.about || ""}
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 my-6">
                <div className="">
                  <p className="text-blackie  font-medium text-xl">
                    Business Name
                  </p>
                  <p className="text-lightBlackText text-base">
                    {event?.host?.businessName || ""}
                  </p>
                </div>
                {event?.host?.businessWebsite && (
                  <div className="">
                    <p className="text-blackie text-xl font-medium ">
                      Business Website
                    </p>
                    <a
                      href={event?.host?.businessWebsite || ""}
                      className="text-blue-600 text-base"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {event?.host?.businessWebsite || ""}
                    </a>
                  </div>
                )}
              </div>
              <div>
                {event?.rsvp && (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-lg w-[193px] h-[217px] bg-white p-3 shadow-md">
                    <img
                      className="w-[110px] h-[110px] rounded-full object-cover"
                      src={getFilePathWithBackendUrl(
                        (event as any)?.rsvp?.profilePicUrl?.fileDownloadUri
                      )}
                      alt="RSVP"
                    />
                    <div className="text-center">
                      <p className="font-medium text-lg text-blackie">RSVP</p>
                      <p className=" text-blackie text-base">{`${event.rsvp.firstName} ${event.rsvp.lastName}`}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="w-full md:w-[40%] ">
            <div className="flex flex-col">
              <h3 className="text-blackie text-xl font-medium mb-4">
                Event Images
              </h3>
              <ImageCarousel
                images={event?.eventPicsUrl || []}
                getImagePath={getFilePathWithBackendUrl}
                height="h-[250px] lg:h-[300px]"
                autoPlayInterval={6000}
                showIndicators={true}
              />
            </div>
          </div>
        </div>
      </div>

      {workshopSessionModal && (
        <WorkshopSessionPopup
          isOpen={workshopSessionModal}
          onClose={() => setWorkshopSessionModal(false)}
          sessions={sortedSessions}
        />
      )}
    </div>
  );
};

export default MinorExperiencesDetail;
